import type { CanvasLinkAttributes } from "./types";
import { combineTransactionSteps, findChildrenInRange, getChangedRanges } from "@tiptap/core";
import Link from "@tiptap/extension-link";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { objectParameterName } from "ls/router/constants";
import { isLinkMark } from "../../utils";

// const createFileUrlPattern = (path: string) => new RegExp(`\\/app\\/${path}\\/(?<shortcut>[a-z0-9]{6})`, 'i');
// const fileUrlPattern = createFileUrlPattern('project');
const fileUrlPattern = /\/app\/project\/(?<shortcut>[a-z0-9]{6})/i;
function parseCanvasLink(href: string): { shortcut: string; objectId: string } | undefined {
  try {
    const url = new URL(href);
    const isFileUrl = url.pathname.match(fileUrlPattern);
    if (!isFileUrl) return;

    const objectId = url.searchParams.get(objectParameterName);
    const shortcut = isFileUrl.groups?.shortcut?.toLowerCase();

    if (objectId && shortcut) {
      return { objectId, shortcut };
    }
    return undefined;
  } catch {
    // ignore errors
    return undefined;
  }
}

export function linkMarkToCanvasLink({ shortcut: getShortcut }: { shortcut: () => string }) {
  return new Plugin({
    key: new PluginKey("linkMarkToCanvasLink"),
    appendTransaction(transactions, oldState, state) {
      const docChanges = transactions.some(transaction => transaction.docChanged) && !oldState.doc.eq(state.doc);
      if (!docChanges) return;

      const canvasLinkSpec = state.schema.nodes.canvasLink;
      const linkMarkSpec = state.schema.marks[Link.name];
      if (!canvasLinkSpec || !linkMarkSpec) return;

      const { tr } = state;
      const transform = combineTransactionSteps(oldState.doc, [...transactions]);
      const changes = getChangedRanges(transform);
      if (!changes.length) return;

      for (const change of changes) {
        const { newRange } = change;
        const linksInChangedRanges = findChildrenInRange(
          state.doc,
          newRange,
          node => node.marks.some(m => m.type === linkMarkSpec),
        );

        for (const linkInChangedRange of linksInChangedRanges) {
          const { node, pos } = linkInChangedRange;
          const linkMark = node.marks.find(m => m.type === linkMarkSpec);

          if (!linkMark || !isLinkMark(linkMark)) continue;

          const href = linkMark.attrs.href;
          const from = tr.mapping.map(pos);
          const to = from + node.nodeSize;

          const parsed = parseCanvasLink(href);
          if (parsed && getShortcut().toLowerCase() === parsed.shortcut) {
            const { objectId } = parsed;
            const attrs: CanvasLinkAttributes = {
              objectId,
            };
            const newNode = canvasLinkSpec.create(attrs);
            tr.replaceRangeWith(from, to, newNode);
          }
        }
      }

      if (!tr.steps.length) {
        return;
      }

      return tr;
    },
  });
}
